import type { CarrierEnabledScope } from '@klareospace/pharos-lib-types';
import { DROPDOWN_FETCH_PAGE_SIZE } from '@klareospace/pharos-lib-front-utils';

import { getCarrierSiteFilterForSpace } from '../../carriers/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Available-drivers filter for the vehicle drawer's default-driver dropdown. */
export interface AvailableDriversFilter {
  carrierId?: string;
  enabled: boolean;
  excludeVehicleId?: string;
  page: number;
  pageSize: number;
  siteIds?: string[];
  spaceId: string;
}

// ─── Carrier-scoped site helpers ─────────────────────────────────────────────

/**
 * Which site IDs to fetch for a carrier's linked-sites selector. Allowlist carriers (`only`)
 * fetch exactly their enabled sites; all-sites carriers (`allExcept`) return `null` = fetch all
 * (disabled ones are removed client-side); a carrier with no scope in the space (`none`) returns
 * `[]` = fetch nothing.
 */
export function carrierSiteQueryIds(
  carrier: { enabledScopes: CarrierEnabledScope[] } | undefined,
  spaceId: string | null,
): string[] | null {
  if (!carrier || !spaceId) return null;
  const filter = getCarrierSiteFilterForSpace(carrier.enabledScopes, spaceId);
  if (filter.mode === 'only') return filter.ids;
  if (filter.mode === 'none') return [];
  return null;
}

/**
 * Drops selected values that are no longer valid options, keeping any `exempt` values (e.g. a
 * persisted assignment kept visible via the escape hatch). Returns the next selection and
 * whether it changed, so callers can dirty the form only on a real change.
 */
export function pruneInvalidSelection(
  selected: string[],
  validValues: Iterable<string>,
  exempt: Iterable<string> = [],
): { changed: boolean; next: string[] } {
  const valid = new Set(validValues);
  const keep = new Set(exempt);
  const next = selected.filter((value) => valid.has(value) || keep.has(value));
  return { changed: next.length !== selected.length, next };
}

/**
 * Builds the available-drivers filter for the vehicle drawer's default-driver dropdown. The list
 * is restricted to the vehicle's carrier and site context, so it never offers drivers from other
 * carriers or from sites where the carrier is not enabled.
 */
export function buildAvailableDriversFilter(input: {
  canList: boolean;
  carrierId: string | null | undefined;
  excludeVehicleId?: string;
  open: boolean;
  siteIds: string[];
  spaceId: string | null;
}): AvailableDriversFilter {
  return {
    carrierId: input.carrierId || undefined,
    enabled: input.open && input.canList && !!input.spaceId,
    excludeVehicleId: input.excludeVehicleId,
    page: 1,
    pageSize: DROPDOWN_FETCH_PAGE_SIZE,
    siteIds: input.siteIds.length > 0 ? input.siteIds : undefined,
    spaceId: input.spaceId ?? '',
  };
}
